import { t } from '@avalara/bat-apps-shared/i18n';
import type { Connector, ConnectorMetadata, TierOffer } from '@avalara/types/index';

type TierValues = {
  1: number;
  2: number;
  3: number;
  4: number;
};

const activationPrices: TierValues = {
  1: 184.63, // from 175.84
  2: 370.44, // from 352.8
  3: 610.34, // from 581.28
  4: 807.91, // from 769.44
};

const quantities: TierValues = {
  1: 250,
  2: 500,
  3: 1000,
  4: 1500,
};

const annualServiceCharges: Record<'soho' | 'pro', TierValues> = {
  soho: {
    1: 61.15, // from 58.24
    2: 116.42, // from 110.88
    3: 178.75, // from 170.24
    4: 246.96, // from 235.2
  },
  pro: {
    1: 426.89, // from 406.56
    2: 854.95, // from 814.24
    3: 1425.31, // from 1357.44
    4: 1925.11, // from 1833.44
  },
};

const overageCharges: Record<'soho' | 'pro', TierValues> = {
  soho: {
    1: 0.49, // from 0.47
    2: 0.46, // from 0.44
    3: 0.36, // from 0.34
    4: 0.33, // from 0.31
  },
  pro: {
    1: 3.41, // from 3.25
    2: 3.42, // from 3.26
    3: 2.85, // from 2.71
    4: 2.56, // from 2.44
  },
};

type PlatformLevel = 'Level 0' | 'Level 1' | 'Level 2' | 'Level 3';

const platformFees: Record<PlatformLevel, TierValues | null> = {
  'Level 0': null,
  'Level 1': null,
  'Level 2': {
    1: 42.34, // from 40.32
    2: 85.85, // from 81.76
    3: 135.24, // from 128.8
    4: 184.63, // from 175.84
  },
  'Level 3': {
    1: 264.6, // from 252
    2: 530.38, // from 505.12
    3: 882.0, // from 840
    4: 1197.17, // from 1140.16
  },
};

type PaidOfferOptions = {
  platformLevel: PlatformLevel;
  tierNumber: 1 | 2 | 3 | 4;
};

const createPaidOffer = ({ platformLevel, tierNumber }: PaidOfferOptions): TierOffer => {
  const platformType = platformLevel === 'Level 1' ? 'soho' : 'pro';
  const annualServiceCharge = annualServiceCharges[platformType][tierNumber];
  const overagePrice = overageCharges[platformType][tierNumber];
  const annualPlatformFee = platformFees[platformLevel]?.[tierNumber] ?? 0;
  const recurringPrice = Number.parseFloat((annualServiceCharge + annualPlatformFee).toFixed(2));
  return {
    id: 'avatax',
    name: 'avatax',
    recurringPrice,
    activationPrice: activationPrices[tierNumber],
    overagePrice,
    // NOTE(Andrew) - waived in this context means that the activation fee is waived:
    waived: platformLevel === 'Level 0' || platformLevel === 'Level 1',
    quantity: quantities[tierNumber],
    tierNumber,
  };
};

const createConnectorMetadata = (platformLevel: PlatformLevel): ConnectorMetadata => ({
  paidOffer1: createPaidOffer({ platformLevel, tierNumber: 1 }),
  paidOffer2: createPaidOffer({ platformLevel, tierNumber: 2 }),
  paidOffer3: createPaidOffer({ platformLevel, tierNumber: 3 }),
  paidOffer4: createPaidOffer({ platformLevel, tierNumber: 4 }),
});

export const CONNECTOR_LEVELS: {
  [connectorLevel: string]: ConnectorMetadata;
} = {
  'Level 0': createConnectorMetadata('Level 0'),
  'Level 1': createConnectorMetadata('Level 1'),
  'Level 2': createConnectorMetadata('Level 2'),
  'Level 3': createConnectorMetadata('Level 3'),
};

// The following maps connector ids to all relevant signup and purchase info,
// for every connector buy.avalara.com currently supports. Anything not on this list
// cannot be sold today!

export const API_CONNECTOR: Connector = {
  activeOptions: [
    'Standard Plus Success Plan',
    'Implementation Services SOW',
    'Business Success Plan',
    'Enterprise Success Plan',
  ],
  helplink: 'http://help.avalara.com/004_AvaTax_Integrations/AvaTax_APIs',
  connectorType: 'NOT a Connector',
  isExtractor: false,
  userImplementationGuide: null,
  vibrancy: 'Established',
  logo: 'https://www.avalara.com/content/dam/avalara/public/logos/integration-logos/avalara-api-logo.png',
  name: 'API / SDK',
  metadata: CONNECTOR_LEVELS['Level 0'],
  versions: [],
  installVideo: null,
  availableForOnlineBuy: true,
  isAvalaraIncluded: false,
  level: 'Level 0',
  salesEngineerRequired: false,
  avalaraProduct: 'AvaTax',
  availableForOnlineTrial: true,
  id: 'a0n4000000ChMxlAAF',
  avaProServicesRequired: false,
  requiredOptions: [],
  turboEnabled: false,
  featuresEnabled: [],
  isAvalaraOwned: false,
  isAvaTaxProduct: true,
  isAgaveProduct: false,
  isVATProduct: false,
  isBevAlcProduct: false,
  isTrustFileProduct: false,
  isCommunicationsProduct: false,
  isExciseFuelProduct: false,
  isLandedCostProduct: false,
  isCertCaptureProduct: false,
  isEInvoicingProduct: false,
};

export const FEATURED_CONNECTORS: Array<string> = [
  'a0n4000000ChMxlAAF', // API
  'a0n4000000ChMvsAAF', // QBD
  'a0n4000000Dv7vAAAR', // QBO
  'a0n4000000ChjR0AAJ', // Vortx
  'a0n4000000ChMvfAAF', // 3DCart
  'a0n3300000FSOpWAAX', // Foxycart
  'a0n4000000ChMtWAAV', // Able Commerce
  'a0n3300000FSK2zAAH', // Woo Commerce
  'a0n4000000ChMx4AAF', // Magento 1
];

export const FEATURED_FREE_TRIAL_CONNECTORS: Array<string> = [
  'a0n4000000Dv7vAAAR', // QBO
  'a0n4000000ChMvsAAF', // QBD
  'a0nUz000003MOA1IAO', // Avalara for Shopify
  'a0n3300000FSK2zAAH', // Woo Commerce
  'a0n3300000Iokv5AAB', // MS Dynamics 365 Finance
  'a0n4000000EnfFtAAJ', // Stripe Invoicing
  'a0n5a00000dAoTjAAK', // BigCommerce
];

export const SOHO_CONNECTORS: Array<string> = [
  'a0n4000000ChMvsAAF', // QBD
  'a0n4000000Dv7vAAAR', // QBO
  'a0n4000000ChMvfAAF', // 3DCart
  'a0n3300000FSK2zAAH', // Woo Commerce
  'a0n4000000ChMxRAAV', // X-Cart
];

const usStates = [
  'AK',
  'AL',
  'AR',
  'AS',
  'AZ',
  'CA',
  'CO',
  'CT',
  'DC',
  'DE',
  'FL',
  'GA',
  'GU',
  'HI',
  'IA',
  'ID',
  'IL',
  'IN',
  'KS',
  'KY',
  'LA',
  'MA',
  'MD',
  'ME',
  'MI',
  'MN',
  'MO',
  'MP',
  'MS',
  'MT',
  'NC',
  'ND',
  'NE',
  'NH',
  'NJ',
  'NM',
  'NV',
  'NY',
  'OH',
  'OK',
  'OR',
  'PA',
  'PR',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UM',
  'UT',
  'VA',
  'VI',
  'VT',
  'WA',
  'WI',
  'WV',
  'WY',
];

const canadaRegions = ['AB', 'BC', 'MB', 'NB', 'NL', 'NT', 'NS', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT'];

export const REGIONS_BY_COUNTRY: {
  [k: string]: Array<string>;
} = {
  Canada: canadaRegions,
  CA: canadaRegions,
  'United States of America': usStates,
  USA: usStates,
  US: usStates,
};

export const fullRegionNames: {
  [k: string]: string;
} = {
  AL: 'Alabama',
  AK: 'Alaska',
  AS: 'American Samoa',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DC: 'Washington, D. C.',
  DE: 'Delaware',
  FL: 'Florida',
  GA: 'Georgia',
  GU: 'Guam',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MP: 'Northern Mariana Islands',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  PR: 'Puerto Rico',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VI: 'U. S. Virgin Islands',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
  // Canada regions
  AB: 'Alberta',
  BC: 'British Columbia',
  MB: 'Manitoba',
  NB: 'New Brunswick',
  NL: 'Newfoundland and Labrador',
  NT: 'Northwest Territories',
  PE: 'Prince Edward Island',
  QC: 'QUEBEC',
  SK: 'Saskatchewan',
};

export const regionCodes = {
  Alabama: 'AL',
  Alaska: 'AK',
  'American Samoa': 'AS',
  Arizona: 'AZ',
  Arkansas: 'AR',
  California: 'CA',
  Colorado: 'CO',
  Connecticut: 'CT',
  'Washington, D. C.': 'DC',
  Delaware: 'DE',
  Florida: 'FL',
  Georgia: 'GA',
  Guam: 'GU',
  Hawaii: 'HI',
  Idaho: 'ID',
  Illinois: 'IL',
  Indiana: 'IN',
  Iowa: 'IA',
  Kansas: 'KS',
  Kentucky: 'KY',
  Louisiana: 'LA',
  Maine: 'ME',
  Maryland: 'MD',
  Massachusetts: 'MA',
  Michigan: 'MI',
  Minnesota: 'MN',
  Mississippi: 'MS',
  Missouri: 'MO',
  Montana: 'MT',
  Nebraska: 'NE',
  Nevada: 'NV',
  'New Hampshire': 'NH',
  'New Jersey': 'NJ',
  'New Mexico': 'NM',
  'New York': 'NY',
  'North Carolina': 'NC',
  'North Dakota': 'ND',
  'Northern Mariana Islands': 'MP',
  Ohio: 'OH',
  Oklahoma: 'OK',
  Oregon: 'OR',
  Pennsylvania: 'PA',
  'Puerto Rico': 'PR',
  'Rhode Island': 'RI',
  'South Carolina': 'SC',
  'South Dakota': 'SD',
  Tennessee: 'TN',
  Texas: 'TX',
  Utah: 'UT',
  Vermont: 'VT',
  Virginia: 'VA',
  'U. S. Virgin Islands': 'VI',
  Washington: 'WA',
  'West Virginia': 'WV',
  Wisconsin: 'WI',
  Wyoming: 'WY',
};

export const convertAbbreviationsToFullNames = (abbreviations: Array<string>): Array<string> =>
  abbreviations
    .map((abbr) => fullRegionNames[abbr])
    .filter(Boolean)
    .sort();

export const convertOneFullNameToAbbreviation = (fullName: keyof typeof regionCodes): string | null =>
  regionCodes[fullName] || null;

export const COLORS = {
  alertRed: '#C70000',
  blue: '#028BBE',
  chartBlue: '#00CBF7',
  chartLightBlue: '#E1F1FA',
  chartGreen: '#C4E43F',
  darkBlue: '#027FAD',
  darkGray: '#5F6062',
  darkOrange: '#FF6600',
  darkerOrange: '#E05200',
  disabledGray: '#D7D7D7',
  grayBlack: '#1A1A1A',
  grayLighter: '#DDD',
  grayLightest: '#F6F6F6',
  green: '#26A23A',
  lightBlue: '#059BD2',
  lighterBlue: '#21B7F9',
  lighterGray: '#F2F3F7',
  lighterMediumDarkGray: '#444444',
  lighterRed: '#DD0226',
  lightestBlue: '#54B6CC',
  lightestGray: '#F5F6FA',
  lightGray: '#E7E7E7',
  lightGreen: '#22AE36',
  lightOrange: '#FFD1B2',
  mediumDarkGray: '#72767E',
  mediumGray: '#CCC',
  mediumGreen: '#1D7B3D',
  meterYellow: '#F4F142',
  orange: '#FC6621',
  paleBlue: '#E4EFF6',
  warningBorderColor: '#FEDB4F',
  warningColor: '#F7D660',
  warningYellow: '#FEDB4F',
  warningTextColor: '#E18E00',
  white: '#FFF',
  lighterBlueSdk: '#CFEDFA',
  lightBlueSdk: '#9BDDFA',
  pubChartBlue: '#015F87',
  pubChartGreen: '#38A632',
  pubChartPurple: '#9F52B3',
  pubChartYellow: '#E5B700',
  pubChartLightGreen: '#9EE69A',
};

export const TERMS_LINKS = {
  general: 'https://legal.avalara.com/#serviceterms',
  site: 'https://www.avalara.com/us/en/legal/site-terms.html',
  avatax: 'https://www.avalara.com/us/en/legal/avatax-terms.html',
  returns: 'https://legal.avalara.com/product-terms.html#returns',
  ps: 'https://www.avalara.com/us/en/legal/ps-terms.html',
  privacy: 'https://www.avalara.com/us/en/legal/privacy-policy.html',
  esow: 'https://www.avalara.com/us/en/legal/professional-services-esow.html',
  str_ps: 'https://legal.avalara.com/#psterms',
  str_esow: 'https://legal.avalara.com/#esow',
  license: 'https://www.avalara.com/us/en/legal/avalara-license-service-terms-and-conditions.html',
  mvr: 'https://www.avalara.com/us/en/legal/mvr-terms.html',
  ioss: 'https://www.avalara.com/us/en/legal/avalara-ioss-intermediary-supplemental-terms.html',
  avataxFreeTrial: 'https://legal.avalara.com/product-terms.html#avatax',
  termsAndConditions: 'https://legal.avalara.com/#serviceterms',
};

const localhostUrl = 'http://localhost:8080';
export const E2E_CONSTANTS = {
  CLIENT_URL: localhostUrl,
  API_URL: localhostUrl,
};

export const returnsProducts = {
  subscriptionPrice: 0,
  pricePerReturn: 72, // unchanged by AAA-10137
  annualPrice: 0,
};

export const riskAssessmentBasePrice = 4500; // unchanged by AAA-10137

export const termsUrls = {
  avatax: 'https://www.avalara.com/us/en/legal/avatax-terms.html',
  cancellation: 'https://knowledge.avalara.com/cancellation',
  esow: 'https://www.avalara.com/us/en/legal/professional-services-esow.html',
  general: TERMS_LINKS.general,
  iossIntermediary: 'https://www.avalara.com/IOSS-intermediary-terms',
  license: 'https://www.avalara.com/us/en/legal/avalara-license-service-terms-and-conditions.html',
  managedVatReporting: 'https://www.avalara.com/mvr-terms.html',
  privacy: 'https://www.avalara.com/us/en/legal/privacy-policy.html',
  ps: 'https://www.avalara.com/us/en/legal/ps-terms.html',
  returns: TERMS_LINKS.returns,
  site: 'https://www.avalara.com/us/en/legal/site-terms.html',
};

export const complianceContent = [
  {
    title: t('shared.headings.learn-where-to-file'),
    textContent:
      'Our sales tax risk assessment tool can help you determine where you have nexus - whether you have a physical storefront, sell online, or both.',
    buttonText: 'Find your nexus',
    link: 'https://www.avalara.com/us/en/learn/nexus/find_your_nexus.html',
  },
  {
    title: t('shared.headings.look-up-tax-rates'),
    textContent:
      'Need to figure out a tax rate for just a couple locations? Our sales tax calculator and rate lookup tool can help.',
    buttonText: 'Find rates by address',
    link: 'https://www.avalara.com/taxrates/en/calculator.html',
  },
  {
    title: t('shared.headings.simplify-compliance'),
    textContent:
      'Automate sales and use tax calculation, document management, and returns preparation under a single platform.',
    buttonText: 'See our solutions',
    link: 'https://www.avalara.com/us/en/products/sales-and-use-tax.html',
  },
];

export const iossContent = [
  {
    title: t('shared.headings.international-tax-solutions'),
    textContent:
      'Discover the wide range of tax, customs, and duty solutions from Avalara for selling into international markets',
    buttonText: 'Learn more',
    link: 'https://www.avalara.com/us/en/products/industry-solutions/cross-border-solutions.html',
  },
  {
    title: t('shared.headings.european-legislation'),
    textContent: 'Stay up to date with the latest news and information on European Union tax legislation with VATLive',
    buttonText: 'Go to VATLive',
    link: 'https://www.avalara.com/vatlive/en/vat-news/category/europe.html',
  },
  {
    title: t('shared.headings.guided-instructions-for-ioss'),
    textContent: 'The Avalara Help Center has step-by-step instructions for completing your IOSS Solution',
    buttonText: 'Get help',
    link: 'https://help.avalara.com/008_VAT_Learning_Center',
  },
];

export const EMEA_RECORD_TYPE_ID = '01233000000MImxAAG';

const emDash = '\u2014';
const euroSymbol = '\u20AC';
export const iossOfferDetails = [
  {
    accordionHeaderText: 'How IOSS benefits your business',
    features: [
      `You're based outside the EU${emDash}IOSS is designed to simplify VAT compliance for non-EU merchants`,
      `You sell mostly low-value goods${emDash}qualifying shipments must total less than ${euroSymbol}150`,
      `You want to grow your business in Europe${emDash}IOSS covers VAT reporting and payment in all 27 EU member states`,
    ],
  },
];

type PartnerIntegrationInfo = {
  partnerCode: string;
  returnUrl: string | null;
  allowedDevUrls: Array<string>;
};

type CreatePartnerMapEntryParams = {
  partnerCode: string;
  connectorId: string;
  shouldShowLicenseKey?: boolean;
  partnerInfo?: PartnerIntegrationInfo;
};

type PartnerInfo = {
  connectorId: string;
  shouldShowLicenseKey: boolean;
  partnerInfo: PartnerIntegrationInfo;
};

// The following maps partner signup codes to their corresponding connector trial/purchase
// configuration. Only supported partner signup integrations are included!

type PartnerMap = {
  [key: string]: PartnerInfo;
};

const genericPartnerInfo = (partnerCode: string) => ({
  partnerCode,
  returnUrl: null,
  allowedDevUrls: [],
});

const ACCTVANTAGE_PARTNER_INFO = {
  partnerCode: 'ACCTVANTAGE',
  returnUrl: 'https://help.avalara.com/Find_Your_Home_Page/AcctVantage_ERP',
  allowedDevUrls: [],
};

const QBO_PARTNER_INFO = {
  partnerCode: 'QBO',
  returnUrl: 'https://www.avataxforqbo.com',
  allowedDevUrls: [
    'https://localhost:44300',
    'https://avataxforqbo.connectorsdev.avatax.com/',
    'https://avataxforqbo.connectorsqa.avatax.com/',
    'https://www.sandbox.avataxforqbo.com',
    'https://www.avataxforqbo.com',
  ],
};

const createPartnerMapEntry = ({
  partnerCode,
  connectorId,
  shouldShowLicenseKey = true,
  partnerInfo,
}: CreatePartnerMapEntryParams) => ({
  [partnerCode]: {
    connectorId,
    shouldShowLicenseKey,
    partnerInfo: partnerInfo ?? genericPartnerInfo(partnerCode),
  },
});

export const PARTNER_SIGNUP_MAP: PartnerMap = {
  ...createPartnerMapEntry({ partnerCode: 'ABANTECART', connectorId: 'a0n3300000GeRTUAA3' }),
  ...createPartnerMapEntry({ partnerCode: 'API', connectorId: 'a0n4000000ChMxlAAF' }),
  ...createPartnerMapEntry({ partnerCode: 'X-CART', connectorId: 'a0n4000000ChMxRAAV' }),
  ...createPartnerMapEntry({
    partnerCode: 'QBO',
    connectorId: 'a0n4000000Dv7vAAAR',
    shouldShowLicenseKey: false,
    partnerInfo: QBO_PARTNER_INFO,
  }),
  ...createPartnerMapEntry({ partnerCode: 'QBD', connectorId: 'a0n4000000ChMvsAAF' }),
  ...createPartnerMapEntry({ partnerCode: 'VORTX', connectorId: 'a0n4000000ChjR0AAJ' }),
  ...createPartnerMapEntry({ partnerCode: 'SHIFT4SHOP', connectorId: 'a0n4000000ChMvfAAF' }),
  ...createPartnerMapEntry({ partnerCode: 'FOXYCART', connectorId: 'a0n3300000FSOpWAAX' }),
  ...createPartnerMapEntry({ partnerCode: 'ABLECOMMERCE', connectorId: 'a0n4000000ChMtWAAV' }),
  ...createPartnerMapEntry({ partnerCode: 'WOOCOMMERCE', connectorId: 'a0n3300000FSK2zAAH' }),
  ...createPartnerMapEntry({ partnerCode: 'MAGENTO', connectorId: 'a0n4000000ChMx4AAF' }),
  ...createPartnerMapEntry({ partnerCode: 'FUSEBILL', connectorId: 'a0n4000000ChMvIAAV' }),
  ...createPartnerMapEntry({ partnerCode: 'EPISERVER', connectorId: 'a0n4000000Dw2jVAAR' }),
  ...createPartnerMapEntry({ partnerCode: 'WEBJAGUAR', connectorId: 'a0n4000000Dw4AtAAJ' }),
  ...createPartnerMapEntry({ partnerCode: 'COLINEAR', connectorId: 'a0n4000000ChMuHAAV' }),
  ...createPartnerMapEntry({ partnerCode: 'LOGICBLOCK', connectorId: 'a0n4000000DsUyJAAV' }),
  ...createPartnerMapEntry({ partnerCode: 'SALESWARP', connectorId: 'a0n3300000F3QIqAAN' }),
  ...createPartnerMapEntry({ partnerCode: 'REVINDEX STOREFRONT', connectorId: 'a0n4000000ChMxZAAV' }),
  ...createPartnerMapEntry({ partnerCode: 'ECC DESKTOP', connectorId: 'a0n4000000ChMydAAF' }),
  ...createPartnerMapEntry({ partnerCode: 'NEXTERNAL ECOMMERCE', connectorId: 'a0n4000000ChMwqAAF' }),
  ...createPartnerMapEntry({ partnerCode: 'SAASOPTICS', connectorId: 'a0n0b00000JhKefAAF' }),
  ...createPartnerMapEntry({ partnerCode: 'BRIGHTSTORES', connectorId: 'a0n3300000Ipu6BAAR' }),
  ...createPartnerMapEntry({ partnerCode: 'SHOPSITE', connectorId: 'a0n4000000ChMxrAAF' }),
  ...createPartnerMapEntry({ partnerCode: 'CHARGEOVER', connectorId: 'a0n4000000EomF6AAJ' }),
  ...createPartnerMapEntry({ partnerCode: 'AMERICOMMERCE', connectorId: 'a0n0b00000KV7yMAAT' }),
  ...createPartnerMapEntry({ partnerCode: 'CHARGEBEE', connectorId: 'a0n4000000DZHnpAAH' }),
  ...createPartnerMapEntry({ partnerCode: 'ULTRACART', connectorId: 'a0n0b00000KT8jZAAT' }),
  ...createPartnerMapEntry({
    partnerCode: 'ACCTVANTAGE',
    connectorId: 'a0n3300000H0AS4AAN',
    partnerInfo: ACCTVANTAGE_PARTNER_INFO,
  }),
  ...createPartnerMapEntry({ partnerCode: 'PINNACLECART', connectorId: 'a0n4000000ChMxFAAV' }),
  ...createPartnerMapEntry({ partnerCode: 'DRUPALCOMMERCE', connectorId: 'a0n3300000F1kP2AAJ' }),
  ...createPartnerMapEntry({ partnerCode: 'DEARSYSTEMS', connectorId: 'a0n0b00000R6IUiAAN' }),
  ...createPartnerMapEntry({ partnerCode: 'CLICKFUNNELS', connectorId: 'a0n0b00000QggJXAAZ' }),
  ...createPartnerMapEntry({ partnerCode: 'PAID-MEMBERSHIPS-PRO', connectorId: 'a0n0b00000KVJ28AAH' }),
  ...createPartnerMapEntry({ partnerCode: 'FASTSIGNS', connectorId: 'a0n5a00000fpg2XAAQ' }),
};

export const getPartnerSignupConnectorByCode = (code: string): PartnerInfo | null => PARTNER_SIGNUP_MAP[code] || null;

export const getPartnerSignupConnectorById = (connectorId: string): PartnerInfo | null =>
  Object.values(PARTNER_SIGNUP_MAP).find((partner) => partner.connectorId === connectorId) || null;
